import React, { useState, useEffect } from 'react';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';
import axiosInstance from "../../axios/axiosInstance";
import toast from 'react-hot-toast';
import {MdAssignmentAdd} from "react-icons/md";
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
} from "../../components/ui";

const Admissions = () => {
    const { t } = useTranslation();
    const [admissions, setAdmissions] = useState([]);
    const [filteredAdmissions, setFilteredAdmissions] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [filters, setFilters] = useState({});
    const [sorter, setSorter] = useState({ field: 'id_visit', order: 'descend' });
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');

    const fetchAdmissions = async (page = 1, pageSize = 10, sortField = 'id_visit', sortOrder = 'descend') => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/visit?page=${page}&size=${pageSize}&status[0]=revisit`);
            console.log(response.data.data);

            const admissionsData = response.data.data.map((item, index) => {
                const serviceId = item.orders.find(order => order.service_type)?.service.id || 'Service ID Not Found';
                console.log(item); // Log each item to inspect its structure
                return {
                    key: index + 1,
                    id_visit: item.id,
                    patient_name: item.patient_id.name,
                    patient_id: item.patient_id.id,
                    doctor_name: item.doctor.name,
                    total_amount: item.total_amount,
                    total_payed: item.total_payed,
                    total_debit: item.total_debit,
                    date_at: item.date_at,
                    status: item.status,
                    service_id: serviceId

                };
            });
            const total = response.data.meta.total;

            const sortedData = admissionsData.sort((a, b) => {
                if (sortField && sortOrder) {
                    const orderMultiplier = sortOrder === 'ascend' ? 1 : -1;
                    if (sortField === 'id_visit' || sortField === 'key') {
                        return (a[sortField] - b[sortField]) * orderMultiplier;
                    }
                    return a[sortField].localeCompare(b[sortField]) * orderMultiplier;
                }
                return 0;
            });

            setAdmissions(sortedData);
            setFilteredAdmissions(sortedData);
            setPagination({ ...pagination, current: page, pageSize, total });
        } catch (error) {
            console.error("Error fetching admissions:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmissions();
    }, []);

    const handleTableChange = (pagination, filters, sorter) => {
        fetchAdmissions(pagination.current, pagination.pageSize, sorter.field, sorter.order);
        setFilters(filters);
        setSorter(sorter);
    };

    const getStatusName = (status) => t(`status.${status}`, { defaultValue: status });

    const createVisit = async (record) => {
        console.log(record); // Ensure the record structure in the console

        try {
            const visitId = record.id_visit; // Access patient_id directly

            const response = await axiosInstance.put(`/visit/revisit/update/${visitId}`);

            toast.success(t('admissions.visitCreated', { name: record.patient_name }));

            // Refresh the admissions list after creating the visit
            fetchAdmissions();
        } catch (error) {
            console.error('Ошибка при создании визита:', error);
            toast.error(t('admissions.visitCreateError'));
        }
    };





    const columns = [
        {
            title: t('common.id'),
            dataIndex: 'key',
            key: 'id',
            width: 72,
            sorter: (a, b) => a.key - b.key,
            sortOrder: sorter.field === 'key' && sorter.order,
            render: (text) => <span className="text-xs text-slate-500 tabular-nums">{text}</span>,
        },
        {
            title: t('common.patient'),
            dataIndex: 'patient_name',
            key: 'patient_name',
            sorter: (a, b) => a.patient_name.localeCompare(b.patient_name),
            sortOrder: sorter.field === 'patient_name' && sorter.order,
            render: (text) => (
                <span className="block text-sm font-medium text-[#1D2C7B] min-w-[150px]">{text}</span>
            ),
        },
        {
            title: t('common.doctor'),
            dataIndex: 'doctor_name',
            key: 'doctor_name',
            sorter: (a, b) => a.doctor_name.localeCompare(b.doctor_name),
            sortOrder: sorter.field === 'doctor_name' && sorter.order,
            render: (text) => <span className="text-sm text-slate-700">{text}</span>,
        },{
            title: t('admissions.revisitDate'),
            dataIndex: 'date_at',
            key: 'date_at',
            sorter: (a, b) => a.date_at.localeCompare(b.date_at),
            sortOrder: sorter.field === 'date_at' && sorter.order,
            width: '20%',
            render: (text) => (
                <span className="text-sm text-slate-700 tabular-nums whitespace-nowrap">{text}</span>
            ),
        },
        {
            title: t('common.actions'),
            key: 'actions',
            width: 190,
            fixed: 'right',
            render: (_, record) => (
                <ActionButton
                    icon={<MdAssignmentAdd size="18"/>}
                    collapseOnMobile={false}
                    className="!h-8 !px-2.5 sm:!px-2.5 !text-xs"
                    onClick={() => createVisit(record)}
                >
                    {t('admissions.addToQueue')}
                </ActionButton>
            ),
        },
    ];

    const handleSearch = (value) => {
        setSearchText(value);
        if (value) {
            const filteredData = admissions.filter((admission) =>
                admission.patient_name.toLowerCase().includes(value.toLowerCase()) ||
                admission.doctor_name.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredAdmissions(filteredData);
        } else {
            setFilteredAdmissions(admissions);
        }
    };

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-[#1D2C7B] truncate">
                        {record.patient_name}
                    </span>
                    <span className="block text-xs text-slate-500 mt-0.5 truncate">{record.doctor_name}</span>
                </div>
                <RowIconButton onClick={() => createVisit(record)} aria-label={t('admissions.addToQueue')}>
                    <MdAssignmentAdd size="18"/>
                </RowIconButton>
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                <span className="tabular-nums">{record.date_at}</span>
            </div>
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('admissions.followups')}
                    count={pagination.total}
                    filters={
                        <Input.Search
                            placeholder={t('common.search')}
                            allowClear
                            size="small"
                            enterButton={t('payments.searchBtn')}
                            onSearch={handleSearch}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="md:!w-[260px]"
                        />
                    }
                />

                <DataTable
                    columns={columns}
                    dataSource={filteredAdmissions}
                    mobileCard={renderMobileCard}
                    rowKey="key"
                    scrollX={960}
                    pagination={{
                        ...pagination,
                        showSizeChanger: false,
                        position: ['bottomCenter'],
                        className: 'px-4 pb-3',
                    }}
                    loading={loading}
                    onChange={handleTableChange}
                />
            </PageCard>
        </PageShell>
    );
};

export default Admissions;
